import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { ProtectedImage } from "../components/ProtectedImage";
import { trackEvent } from "../lib/analytics";

const AUTOPLAY_MS = 5000;

function ProjectCarousel({ project }) {
  const images = useMemo(() => {
    const g = project?.gallery || [];
    return g.length ? g : [project?.cover_image].filter(Boolean);
  }, [project]);

  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef(null);
  const thumbsRef = useRef(null);

  const go = (nextIdx, direction) => {
    setDir(direction);
    setIndex(((nextIdx % images.length) + images.length) % images.length);
  };

  const next = () => go(index + 1, 1);
  const prev = () => go(index - 1, -1);

  useEffect(() => {
    if (!playing || images.length <= 1) return;
    timerRef.current = setTimeout(() => go(index + 1, 1), AUTOPLAY_MS);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, playing, images.length]);

  // Keep the active thumbnail in view
  useEffect(() => {
    const el = thumbsRef.current?.querySelector(`[data-thumb="${index}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [index]);

  const variants = {
    enter: (d) => ({ x: d > 0 ? "6%" : "-6%", opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? "-6%" : "6%", opacity: 0 }),
  };

  if (!images.length) return <div className="md:col-span-8" />;

  return (
    <div
      className="md:col-span-8 md:order-1 order-1"
      onMouseEnter={() => setPlaying(false)}
      onMouseLeave={() => setPlaying(true)}
      data-testid="project-carousel"
    >
      {/* Main frame */}
      <div className="relative clip-frame bg-sand-dark aspect-[4/3] overflow-hidden">
        <AnimatePresence initial={false} custom={dir} mode="popLayout">
          <motion.div
            key={index}
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0"
          >
            <ProtectedImage
              src={images[index]}
              alt={`${project.title} — ${project.category} in ${project.location} by Blue Green Studio (${index + 1})`}
              imgClassName="w-full h-full object-cover"
              className="w-full h-full"
              loading="eager"
            />
          </motion.div>
        </AnimatePresence>

        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              data-testid="carousel-prev"
              aria-label="Previous"
              className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-[5] w-11 h-11 rounded-full bg-sand/90 backdrop-blur text-ink hover:bg-forest hover:text-sand transition-colors flex items-center justify-center"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              onClick={next}
              data-testid="carousel-next"
              aria-label="Next"
              className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-[5] w-11 h-11 rounded-full bg-sand/90 backdrop-blur text-ink hover:bg-forest hover:text-sand transition-colors flex items-center justify-center"
            >
              <ChevronRight size={20} />
            </button>

            <div className="absolute bottom-4 left-4 z-[5] flex items-center gap-3 bg-sand/90 backdrop-blur px-3 py-1.5">
              <button
                type="button"
                onClick={() => setPlaying((p) => !p)}
                data-testid="carousel-toggle-play"
                aria-label={playing ? "Pause" : "Play"}
                className="text-ink hover:text-teal transition-colors"
              >
                {playing ? <Pause size={14} /> : <Play size={14} />}
              </button>
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink">
                {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div
          ref={thumbsRef}
          data-testid="carousel-thumbs"
          className="mt-4 flex gap-2 md:gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin"
        >
          {images.map((img, i) => (
            <button
              key={img + i}
              data-testid={`carousel-thumb-${i}`}
              data-thumb={i}
              onClick={() => go(i, i > index ? 1 : -1)}
              className={`shrink-0 snap-start w-24 md:w-28 aspect-[4/3] overflow-hidden bg-sand-dark border transition-all ${
                i === index ? "border-forest opacity-100 ring-2 ring-forest/40" : "border-ink/10 opacity-70 hover:opacity-100"
              }`}
              aria-label={`Show image ${i + 1}`}
            >
              <ProtectedImage src={img} alt="" imgClassName="w-full h-full object-cover" className="w-full h-full" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/projects/${id}`)
      .then((r) => {
        setProject(r.data);
        trackEvent("project_view", { project_id: r.data.id, project_title: r.data.title, category: r.data.category });
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6" data-testid="project-not-found">
        <p className="font-serif text-4xl text-ink">Project not found</p>
        <Link to="/" className="link-underline text-xs uppercase tracking-[0.2em] font-bold text-clay">Back home</Link>
      </div>
    );

  if (!project)
    return <div className="min-h-screen flex items-center justify-center text-stone">Loading…</div>;

  const meta = [
    { label: "Category", value: project.category },
    { label: "Location", value: project.location },
    { label: "Year", value: project.year },
  ];

  const faqs = [
    {
      q: `Where is ${project.title} located?`,
      a: `${project.title} is a Blue Green Studio residence located in ${project.location}.`,
    },
    {
      q: `When was ${project.title} completed?`,
      a: `${project.title} was designed and completed in ${project.year} by Blue Green Studio, Bangalore.`,
    },
    {
      q: `What kind of project is ${project.title}?`,
      a: `${project.title} is a ${project.category.toLowerCase()} project. ${project.description}`,
    },
    {
      q: "Can I commission a home in this style from Blue Green Studio?",
      a: "Yes. Every Blue Green Studio residence is a bespoke response to its site, climate and client. Start with our two-minute design quiz and we will respond within two working days.",
    },
  ];

  return (
    <div data-testid="project-detail-page" className="pt-32 md:pt-40">
      <Seo
        title={`${project.title} — ${project.category} in ${project.location}`}
        description={`${project.title} by Blue Green Studio — a ${project.category.toLowerCase()} project in ${project.location} (${project.year}). ${project.description.slice(0, 140)}`}
        image={project.cover_image}
        path={`/projects/${project.id}`}
        type="article"
        keywords={[
          `${project.category} architecture ${project.location}`,
          `${project.title}`,
          `residential architects ${project.location}`,
          `${project.category.toLowerCase()} design Bangalore`,
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CreativeWork",
              name: project.title,
              description: project.description,
              image: [project.cover_image, ...(project.gallery || [])],
              creator: {
                "@type": "Organization",
                name: "Blue Green Studio",
                url: typeof window !== "undefined" ? window.location.origin : "",
              },
              dateCreated: project.year,
              contentLocation: { "@type": "Place", name: project.location },
              genre: project.category,
            },
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }}
      />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <Link to="/#work" data-testid="back-to-work" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-stone hover:text-clay transition-colors mb-12">
          <ArrowLeft size={16} /> All Work
        </Link>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-serif text-6xl sm:text-7xl lg:text-[8vw] leading-[0.85] tracking-tighter text-ink"
        >
          {project.title}
        </motion.h1>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 mt-16 grid md:grid-cols-12 gap-12 md:gap-16">
        {/* LEFT — Carousel */}
        <ProjectCarousel project={project} />

        {/* RIGHT — Meta + description */}
        <aside className="md:col-span-4 md:order-2 order-2">
          <div className="md:sticky md:top-32 space-y-8">
            <div className="grid grid-cols-3 gap-6">
              {meta.map((m) => (
                <div key={m.label}>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-clay mb-2">{m.label}</p>
                  <p className="text-ink text-base md:text-lg">{m.value}</p>
                </div>
              ))}
            </div>
            <div className="h-px bg-ink/10" />

            {project.about && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-teal mb-3">About</p>
                <p className="text-ink text-base md:text-lg font-serif italic leading-relaxed">{project.about}</p>
              </div>
            )}

            {project.description && (
              <p className="text-stone text-base md:text-lg leading-relaxed whitespace-pre-line">{project.description}</p>
            )}
          </div>
        </aside>
      </div>

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pb-32 mt-16" data-testid="project-faq">
        <Reveal className="mb-10">
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-teal mb-4">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-ink">About this project</h2>
        </Reveal>
        <div className="border-t border-ink/10 max-w-3xl">
          {faqs.map((f, i) => (
            <details key={i} className="group border-b border-ink/10 py-6" data-testid={`project-faq-${i}`}>
              <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                <h3 className="font-serif text-xl md:text-2xl text-ink group-hover:text-teal transition-colors flex-1">{f.q}</h3>
                <span className="text-teal text-2xl leading-none transition-transform duration-500 group-open:rotate-45">+</span>
              </summary>
              <p className="mt-4 text-stone text-base leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
